import { Rule, Schedule } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule.pb';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { RRule, rrulestr } from 'rrule';
import { ScheduleAvailabilityTypes } from '@frontend/api-schedule-availability';
import { formatDate } from '@frontend/date';
import { i18next } from '@frontend/i18n';
import { AppointmentHour } from './types';

dayjs.extend(utc);

const removeLeadingZero = (time: string) => {
  return time.replace(/^0/, '');
};

const sortByStartTime = (a: string, b: string) => {
  const [aStart] = a.split(' to ');
  const [bStart] = b.split(' to ');
  return dayjs(aStart, 'h:mm A').isBefore(dayjs(bStart, 'h:mm A')) ? -1 : 1;
};

const weekdayToNumberMap: Record<ScheduleAvailabilityTypes.Weekday, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

/**
 * @desc: Interface representing schedule information extracted from an RRule string
 */
interface IScheduleInfo {
  startTime: string;
  endTime: string;
  daysOfWeek: number[];
}

/**
 * Extracts schedule information from an RRule string.
 *
 * @param {string} rrule - The recurrence rule string.
 * @param {number} [duration=0] - The duration in minutes for the event.
 * @param {'HH:mm' | 'hh:mm A'} timeFormat - The format for the start and end times.
 * @returns {IScheduleInfo} An object containing the start time, end time, and days of the week.
 */
const getScheduleInfoFromRRule = (rrule: string, duration = 0, timeFormat: 'HH:mm' | 'hh:mm A'): IScheduleInfo => {
  const rule = RRule.fromString(rrule);

  const dtStart = rule.options.dtstart;
  const startTime = formatDate(dtStart, timeFormat, true);
  const endTime = startTime ? dayjs(startTime, timeFormat).add(duration, 'm').format(timeFormat) : '';

  // rrule - MO: 0, TU: 1, WE: 2, TH: 3, FR: 4, SA: 5, SU: 6
  // dayjs - MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6, SU: 0,

  // Convert RRule weekdays to dayjs weekdays
  const daysOfWeek = rule.options.byweekday?.map((weekday: number) => (weekday === 6 ? 0 : weekday + 1));

  return {
    startTime,
    endTime,
    daysOfWeek,
  };
};

export enum ExceptionStatus {
  Available = 'Available',
  Unavailable = 'Unavailable',
}

type ExceptionsInfo = {
  id: string;
  locationId: string;
  name: string;
  status: ExceptionStatus;
  startDateTime?: string;
  endDateTime?: string;
  duration: number;
};

/**
 * @desc: Function to extract the date range from an RRule string
 * @param rRuleString : string - The RRule string
 * @returns : Object : { startDatetime?: Date; endDatetime?: Date }
 */
const getDateRangeFromRRule = (
  rRuleString?: string
): { startDateTime?: string; endDateTime?: string; tzid?: string | null } => {
  if (!rRuleString) return {};

  const rRuleObj = rrulestr(rRuleString);
  const { dtstart, count } = rRuleObj.options;

  return {
    startDateTime: dayjs.utc(dtstart).toISOString(),
    endDateTime: count
      ? dayjs
          .utc(dtstart)
          .add(count - 1, 'days')
          .toISOString()
      : undefined,
    tzid: rRuleObj.options.tzid,
  };
};

const formatOverride = ({
  duration = 0,
  id = '',
  locationId,
  name = '',
  rrule = '',
}: Rule & { locationId: string }): Omit<ExceptionsInfo, 'status'> => {
  const { startDateTime, endDateTime } = getDateRangeFromRRule(rrule);
  return {
    duration,
    endDateTime,
    id,
    locationId,
    name,
    startDateTime,
  };
};

type ScheduleInfo = IScheduleInfo & { locationId: string };

type FormattedSchedule = {
  breaks: ScheduleInfo[];
  exceptions: ExceptionsInfo[];
  workingHours: ScheduleInfo[];
};

export const formatScheduleV3 = (schedule?: Schedule[]) => {
  if (!schedule || schedule.length === 0) return [];
  const recurrenceRules = schedule.map((s) => {
    const locationId = s.locationId || '';
    // add locationId to each rule for filtering later
    return {
      ...s.recurrenceRules,
      breaks: (s.recurrenceRules?.breaks || []).map((b) => ({ ...b, locationId })),
      override: {
        ...s.recurrenceRules?.override,
        availabilities: (s.recurrenceRules?.override?.availabilities || []).map((a) => ({ ...a, locationId })),
        unAvailabilities: (s.recurrenceRules?.override?.unAvailabilities || []).map((u) => ({ ...u, locationId })),
      },
      workingHours: (s.recurrenceRules?.workingHours || []).map((w) => ({ ...w, locationId })),
    };
  });

  if (!recurrenceRules || recurrenceRules.length === 0) return [];
  return recurrenceRules.map((rule) => {
    const breaks = (rule?.breaks || [])
      .map((b) =>
        b.rrule ? { locationId: b.locationId, ...getScheduleInfoFromRRule(b.rrule, b.duration, 'hh:mm A') } : undefined
      )
      .filter((b): b is ScheduleInfo => !!b);
    const workingHours = (rule?.workingHours || [])
      .map((w) =>
        w.rrule ? { locationId: w.locationId, ...getScheduleInfoFromRRule(w.rrule, w.duration, 'hh:mm A') } : undefined
      )
      .filter((w): w is ScheduleInfo => !!w);
    const availabilities: ExceptionsInfo[] = (rule?.override?.availabilities || []).map((override) => {
      return {
        ...formatOverride(override),
        status: ExceptionStatus.Available,
      };
    });
    const unavailabilities: ExceptionsInfo[] = (rule?.override?.unAvailabilities || []).map((override) => {
      return {
        ...formatOverride(override),
        status: ExceptionStatus.Unavailable,
      };
    });
    const exceptions = [...availabilities, ...unavailabilities].sort((a, b) => {
      if (a.startDateTime && b.startDateTime) {
        return dayjs(a.startDateTime).isBefore(dayjs(b.startDateTime)) ? -1 : 1;
      }
      return 0;
    });
    return { breaks, exceptions, workingHours };
  });
};

export const formatV3AppointmentHours = (
  day: ScheduleAvailabilityTypes.Weekday,
  formattedSchedule: FormattedSchedule[],
  locationId: string
) => {
  const label = day.charAt(0).toUpperCase() + day.slice(1);
  const dayNumber = weekdayToNumberMap[day];
  return formattedSchedule
    .map((schedule) => {
      const workingHours = schedule.workingHours.filter(
        (wh) => wh.daysOfWeek?.includes(dayNumber) && wh.locationId === locationId
      );
      const breaks = schedule.breaks.filter((b) => b.daysOfWeek?.includes(dayNumber) && b.locationId === locationId);
      return { workingHours, breaks };
    })
    .reduce<AppointmentHour[]>((acc, cur) => {
      const formattedWorkingHours = cur.workingHours
        .map((wh) => removeLeadingZero(wh.startTime) + ' to ' + removeLeadingZero(wh.endTime))
        .sort(sortByStartTime);
      const formattedBreaks = cur.breaks
        .map((b) => removeLeadingZero(b.startTime) + ' to ' + removeLeadingZero(b.endTime))
        .sort(sortByStartTime);
      const hasBreaks = formattedBreaks.length > 0;
      const isClosed = formattedWorkingHours.length === 0;
      const dayHasClosedAlready = acc.some((a) => a.isClosed && label === a.label);

      return hasBreaks
        ? [
            ...acc,
            { label, value: formattedWorkingHours.join(', '), isBreak: false, isClosed },
            { label: i18next.t('Break', { ns: 'settings' }), value: formattedBreaks.join(', '), isBreak: true },
          ]
        : dayHasClosedAlready // if isClosed already exists for this day, do not add another
        ? acc
        : [...acc, { label, value: formattedWorkingHours.join(', '), isBreak: false, isClosed }];
    }, []);
};

export const getFormattedExceptionDateRange = (startDate?: string, endDate?: string): string => {
  if (!startDate || !endDate) return '';

  let formattedDate = '';

  const startDateTime = dayjs.utc(startDate);
  const endDateTime = dayjs.utc(endDate);

  const hasSameStartEndDate = startDateTime.isSame(endDateTime, 'day');

  if (hasSameStartEndDate) {
    formattedDate = startDateTime.format('MMMM D, YYYY');
  } else {
    const isSameYear = startDateTime.isSame(endDateTime, 'year');
    const isSameMonth = startDateTime.isSame(endDateTime, 'month');

    if (!isSameYear) {
      formattedDate = `${startDateTime.format('MMMM D, YYYY')} - ${endDateTime.format('MMMM D, YYYY')}`;
    } else if (!isSameMonth) {
      formattedDate = `${startDateTime.format('MMMM D')} - ${endDateTime.format('MMMM D, YYYY')}`;
    } else {
      formattedDate = `${startDateTime.format('MMMM D')} - ${endDateTime.format('D, YYYY')}`;
    }
  }

  return formattedDate;
};

export const getFormattedExceptionTimeRange = (startDate?: string, duration?: number): string => {
  if (!startDate || !duration) return '';

  let formattedTime = '';
  const startDateTime = dayjs.utc(startDate);
  const endDateTime = startDateTime.add(duration, 'minute');

  const isEntireDay =
    startDateTime.hour() === 0 &&
    startDateTime.minute() === 0 &&
    endDateTime.hour() === 23 &&
    endDateTime.minute() === 59;

  if (isEntireDay) {
    formattedTime = 'All Day';
  } else {
    const startTime = startDateTime;
    const endTime = startTime.add(duration, 'minute');
    formattedTime = `${startTime.format('h:mm A')} - ${endTime.format('h:mm A')}`;
  }

  return formattedTime;
};
